import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "@/integrations/supabase/client";
import { PROJECTS, WORKFLOW_SEQUENCE, PROJECT_CATEGORIES, SPECIALIST_LANES } from "../config/wiseboardConfig";

interface CaseData {
  title: string;
  boardQuestion: string;
  projects: { project_type: string; status: string; started_at: string | null; completed_at: string | null }[];
  evidence: { hypothesis_id: string; claim: string; status: string; confidence: number | null; source: string | null; pass_number: number; updated_by_project: string | null; caveat: string | null }[];
  artifacts: { project_id: string; name: string; version: number; approval_status: string; confidence: number | null; content: string | null }[];
  roster: { lane: string; selected: boolean; reason: string | null }[];
}

export async function exportCasePdf(caseId: string) {
  // Fetch all data in parallel
  const [caseRes, projRes, evidenceRes, rosterRes] = await Promise.all([
    supabase.from("wiseboard_cases").select("title, board_question, created_at").eq("id", caseId).single(),
    supabase.from("wiseboard_projects").select("id, project_type, status, started_at, completed_at").eq("case_id", caseId),
    supabase.from("wiseboard_evidence_matrix").select("*").eq("case_id", caseId).order("hypothesis_id"),
    supabase.from("wiseboard_specialist_roster").select("lane, selected, reason").eq("case_id", caseId),
  ]);

  if (!caseRes.data) throw new Error("Case not found");

  // Fetch artifacts for all projects
  const projectIds = (projRes.data || []).map(p => p.id);
  const { data: allArtifacts } = projectIds.length > 0
    ? await supabase.from("wiseboard_artifacts").select("*").in("project_id", projectIds).order("version", { ascending: false })
    : { data: [] };

  const caseData: CaseData = {
    title: caseRes.data.title,
    boardQuestion: caseRes.data.board_question || "",
    projects: projRes.data || [],
    evidence: evidenceRes.data || [],
    artifacts: allArtifacts || [],
    roster: rosterRes.data || [],
  };

  const projectIdToType: Record<string, string> = {};
  caseData.projects.forEach(p => { projectIdToType[(p as any).id] = p.project_type; });

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = margin;

  const addPage = () => { doc.addPage(); y = margin; };
  const checkSpace = (needed: number) => { if (y + needed > 275) addPage(); };

  // ── TITLE PAGE ──
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("WiseBoard Case Report", pageW / 2, 60, { align: "center" });

  doc.setFontSize(16);
  doc.setFont("helvetica", "normal");
  doc.text(caseData.title, pageW / 2, 80, { align: "center" });

  if (caseData.boardQuestion) {
    doc.setFontSize(11);
    doc.setTextColor(100);
    const lines = doc.splitTextToSize(`Board Question: ${caseData.boardQuestion}`, pageW - 40);
    doc.text(lines, pageW / 2, 95, { align: "center" });
    doc.setTextColor(0);
  }

  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-GB")} ${new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`, pageW / 2, 120, { align: "center" });
  doc.setTextColor(0);

  // ── PIPELINE STATUS ──
  addPage();
  y = margin;
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("1. Pipeline Status", margin, y);
  y += 10;

  const statusMap: Record<string, string> = {};
  caseData.projects.forEach(p => { statusMap[p.project_type] = p.status; });

  const pipelineRows = WORKFLOW_SEQUENCE.map(pid => {
    const proj = PROJECTS[pid];
    if (!proj) return null;
    const status = statusMap[pid] || "not_started";
    const cat = PROJECT_CATEGORIES[proj.category];
    return [
      pid.replace("_", " "),
      proj.name,
      cat.label,
      status.replace("_", " ").toUpperCase(),
    ];
  }).filter(Boolean) as string[][];

  autoTable(doc, {
    startY: y,
    head: [["Project", "Name", "Category", "Status"]],
    body: pipelineRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: "bold" },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 3) {
        const val = (data.cell.raw as string) || "";
        if (val === "APPROVED") { data.cell.styles.textColor = [22, 163, 74]; data.cell.styles.fontStyle = "bold"; }
        else if (val === "IN PROGRESS") { data.cell.styles.textColor = [37, 99, 235]; }
        else if (val === "HOLD") { data.cell.styles.textColor = [220, 38, 38]; data.cell.styles.fontStyle = "bold"; }
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  // Summary stats
  const approved = caseData.projects.filter(p => p.status === "approved").length;
  const inProgress = caseData.projects.filter(p => p.status === "in_progress").length;
  const total = caseData.projects.length;

  checkSpace(20);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Summary: ${approved}/${total} approved · ${inProgress} in progress`, margin, y);
  y += 10;

  // ── SPECIALIST ROSTER ──
  checkSpace(30);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("2. Specialist Roster", margin, y);
  y += 10;

  const rosterRows = SPECIALIST_LANES.map(lane => {
    const r = caseData.roster.find(x => x.lane === lane);
    const proj = PROJECTS[lane];
    return [
      lane,
      proj?.name || lane,
      r?.selected ? "SELECTED" : "EXCLUDED",
      r?.reason || "—",
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [["Lane", "Name", "Status", "Reason"]],
    body: rosterRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: "bold" },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 2) {
        const val = (data.cell.raw as string) || "";
        if (val === "SELECTED") { data.cell.styles.textColor = [22, 163, 74]; data.cell.styles.fontStyle = "bold"; }
        else { data.cell.styles.textColor = [156, 163, 175]; }
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  // ── EVIDENCE MATRIX ──
  addPage();
  y = margin;
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("3. Evidence Matrix", margin, y);
  y += 10;

  if (caseData.evidence.length === 0) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.text("No evidence items recorded.", margin, y);
    y += 10;
  } else {
    const evidenceRows = caseData.evidence.map(e => [
      e.hypothesis_id,
      e.claim.length > 60 ? e.claim.substring(0, 57) + "..." : e.claim,
      e.status.replace("_", " "),
      e.confidence != null ? `${e.confidence}%` : "—",
      e.source || "—",
      `P${e.pass_number}`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Hypothesis", "Claim", "Status", "Conf.", "Source", "Pass"]],
      body: evidenceRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [22, 163, 74], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 55 },
        2: { cellWidth: 30 },
        3: { cellWidth: 15 },
        4: { cellWidth: 30 },
        5: { cellWidth: 12 },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 2) {
          const val = (data.cell.raw as string) || "";
          if (val.includes("FOUND") && !val.includes("NOT")) { data.cell.styles.textColor = [22, 163, 74]; }
          else if (val.includes("NOT")) { data.cell.styles.textColor = [220, 38, 38]; }
          else if (val.includes("STALE")) { data.cell.styles.textColor = [202, 138, 4]; }
          else if (val.includes("ASSUMPTION")) { data.cell.styles.textColor = [107, 114, 128]; }
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // Evidence summary
    const stats = {
      found: caseData.evidence.filter(e => e.status === "DATA_FOUND").length,
      notFound: caseData.evidence.filter(e => e.status === "NOT_FOUND").length,
      stale: caseData.evidence.filter(e => e.status === "POTENTIALLY_STALE").length,
      assumptions: caseData.evidence.filter(e => e.status === "ASSUMPTION").length,
    };
    checkSpace(15);
    doc.setFontSize(9);
    doc.text(`Evidence Summary: ${stats.found} found · ${stats.notFound} not found · ${stats.stale} stale · ${stats.assumptions} assumptions`, margin, y);
    y += 10;
  }

  // ── COMPLIANCE TRAFFIC LIGHT ──
  checkSpace(30);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("4. Compliance Traffic Light", margin, y);
  y += 10;

  const complianceRows = WORKFLOW_SEQUENCE.map(pid => {
    const proj = PROJECTS[pid];
    if (!proj) return null;
    const status = statusMap[pid] || "not_started";
    const laneEvidence = caseData.evidence.filter(e => e.updated_by_project === pid);
    const assumptions = laneEvidence.filter(e => e.status === "ASSUMPTION").length;
    const missing = laneEvidence.filter(e => e.status === "NOT_FOUND").length;
    const stale = laneEvidence.filter(e => e.status === "POTENTIALLY_STALE").length;

    // Find artifact confidence
    const dbProjId = caseData.projects.find(p => p.project_type === pid) as any;
    const artConf = dbProjId ? caseData.artifacts.find(a => a.project_id === dbProjId.id && a.approval_status === "approved")?.confidence : null;
    const lowConf = artConf != null && artConf < 50;

    let light = "GREEN";
    if (stale > 0 || missing > 0) light = "AMBER";
    if ((missing > 0 && status === "approved") || lowConf || assumptions >= 3) light = "RED";
    if (status === "not_started") light = "—";

    const issues: string[] = [];
    if (lowConf) issues.push("Low confidence (<50%)");
    if (assumptions >= 3) issues.push(`${assumptions} assumptions`);
    if (missing > 0 && status === "approved") issues.push(`${missing} missing items (approved)`);
    if (stale > 0) issues.push(`${stale} stale`);

    return [pid.replace("_", " "), proj.name, light, issues.join("; ") || "—"];
  }).filter(Boolean) as string[][];

  autoTable(doc, {
    startY: y,
    head: [["Project", "Name", "Light", "Issues"]],
    body: complianceRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: "bold" },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 2) {
        const val = (data.cell.raw as string) || "";
        if (val === "GREEN") { data.cell.styles.textColor = [22, 163, 74]; data.cell.styles.fontStyle = "bold"; }
        else if (val === "AMBER") { data.cell.styles.textColor = [202, 138, 4]; data.cell.styles.fontStyle = "bold"; }
        else if (val === "RED") { data.cell.styles.textColor = [220, 38, 38]; data.cell.styles.fontStyle = "bold"; }
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  // ── BOARD DECISION (P21) ──
  const p21Project = caseData.projects.find(p => p.project_type === "P21") as any;
  const p21Artifact = p21Project
    ? caseData.artifacts.find(a => a.project_id === p21Project.id && a.approval_status === "approved")
    : null;

  checkSpace(40);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("5. Board Decision (P21)", margin, y);
  y += 10;

  if (!p21Artifact || !p21Artifact.content) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.text("No approved board decision yet.", margin, y);
    y += 8;
  } else {
    // Try to parse structured decision
    try {
      const decision = JSON.parse(p21Artifact.content);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      const stance = decision.stance || "N/A";
      if (stance === "Go") doc.setTextColor(22, 163, 74);
      else if (stance === "No-Go") doc.setTextColor(220, 38, 38);
      else if (stance === "Pilot") doc.setTextColor(37, 99, 235);
      else doc.setTextColor(202, 138, 4);
      doc.text(`Stance: ${stance}`, margin, y);
      doc.setTextColor(0);
      y += 8;

      // Evidence points
      if (decision.evidencePoints?.length) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("MECE Evidence Points:", margin, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        decision.evidencePoints.forEach((pt: string, i: number) => {
          checkSpace(10);
          const lines = doc.splitTextToSize(`${i + 1}. ${pt}`, pageW - 2 * margin);
          doc.text(lines, margin + 3, y);
          y += lines.length * 4.5 + 2;
        });
      }

      if (decision.dissent) {
        checkSpace(15);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Dissent:", margin, y); y += 5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const dLines = doc.splitTextToSize(decision.dissent, pageW - 2 * margin);
        doc.text(dLines, margin + 3, y);
        y += dLines.length * 4.5 + 4;
      }

      if (decision.weakestLink) {
        checkSpace(15);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Weakest Link:", margin, y); y += 5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const wLines = doc.splitTextToSize(decision.weakestLink, pageW - 2 * margin);
        doc.text(wLines, margin + 3, y);
        y += wLines.length * 4.5 + 4;
      }

      if (decision.conditions) {
        checkSpace(15);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Conditions Precedent:", margin, y); y += 5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const cLines = doc.splitTextToSize(decision.conditions, pageW - 2 * margin);
        doc.text(cLines, margin + 3, y);
        y += cLines.length * 4.5 + 4;
      }
    } catch {
      // Plain text content
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      const contentLines = doc.splitTextToSize(p21Artifact.content, pageW - 2 * margin);
      const maxLines = Math.min(contentLines.length, 40);
      for (let i = 0; i < maxLines; i++) {
        checkSpace(6);
        doc.text(contentLines[i], margin, y);
        y += 4.5;
      }
      if (contentLines.length > 40) {
        doc.text("... [truncated]", margin, y);
        y += 6;
      }
    }
  }

  // ── FOOTER on each page ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(`WiseBoard Case Report — ${caseData.title} — Page ${i}/${pageCount}`, pageW / 2, 290, { align: "center" });
    doc.setTextColor(0);
  }

  doc.save(`WiseBoard_${caseData.title.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
